import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowUpDown, CalendarDays, Check, ChevronDown, Filter, Plus, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import { apiFetch } from '../../lib/api';
import PortalAvatar from '../../components/profile/PortalAvatar';
import { convertFromUTC, convertToUTC, getUserTimezone } from '../../utils/timezone';
import {
  enqueueTicketMutation,
  listReadyTicketMutations,
  markTicketMutationDone,
  markTicketMutationRetry,
  TicketMutationRecord,
} from '../../lib/ticketsOutbox';
import { subscribeTicketsRealtime } from '../../lib/ticketsRealtime';

import type { AppDispatch } from '../../store';
import type { TicketStatus } from '../../store/features/tickets/types';
import {
  fetchTickets,
  selectAllTickets,
  ticketReplaced,
  ticketRemoved,
  ticketUpserted,
} from '../../store/features/tickets/ticketsSlice';
import { fetchProjects } from '../../store/features/projects/projectsSlice';
import { openTicketPortal } from '../../store/features/ticketPortal/ticketPortalSlice';

const CURRENT_STATUSES = 'open,in_progress,blocked,resolved';
type BoardStatus = Extract<TicketStatus, 'open' | 'in_progress' | 'blocked' | 'resolved'>;
type PriorityFilter = 'all' | 'P0' | 'P1' | 'P2' | 'P3';
type SortMode = 'due_asc' | 'due_desc' | 'priority_desc' | 'title_asc';
type TicketAssigneeOption = {
  uid: string;
  name: string;
  email: string;
  photoURL?: string | null;
  title?: string | null;
  role?: string | null;
};

const BOARD_COLUMNS: Array<{ key: BoardStatus; label: string; empty: string }> = [
  { key: 'open', label: 'Planning', empty: 'No planning tickets' },
  { key: 'in_progress', label: 'In Progress', empty: 'No tickets in progress' },
  { key: 'blocked', label: 'Blockers', empty: 'No blockers' },
  { key: 'resolved', label: 'Complete', empty: 'No completed tickets' },
];

const PRIORITY_FILTER_OPTIONS: Array<{ value: PriorityFilter; label: string }> = [
  { value: 'all', label: 'All priorities' },
  { value: 'P0', label: 'P0 - Urgent' },
  { value: 'P1', label: 'P1 - High' },
  { value: 'P2', label: 'P2 - Medium' },
  { value: 'P3', label: 'P3 - Low' },
];

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'due_asc', label: 'Due date (soonest)' },
  { value: 'due_desc', label: 'Due date (latest)' },
  { value: 'priority_desc', label: 'Priority (high to low)' },
  { value: 'title_asc', label: 'Title (A to Z)' },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: 'easeOut' },
  },
};

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Heading = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  margin: 0;
  color: ${({ theme }) => theme.colors.heading};
  font-weight: 800;
`;

const Subtitle = styled.p`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  color: #9aa4b2;
  font-size: 1rem;
  font-weight: 700;
`;

const Summary = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 8px 12px;
  color: #d4d9e3;
  font-size: 0.86rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    align-items: stretch;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
`;

const PrimaryAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  color: #e8eaed;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 0.86rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.75);
    color: #e0a15e;
  }
`;

const MenuWrap = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.03);
  color: #d5dbe6;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

const MenuLabel = styled.span`
  color: #8f9aae;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 0.74rem;
`;

const MenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 40;
  min-width: 240px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(18, 22, 34, 0.98), rgba(10, 14, 25, 0.98));
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.45);
  padding: 6px;
  display: grid;
  gap: 4px;
`;

const MenuOption = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.6)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.15)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#e0a15e' : '#d6dbe5')};
  border-radius: 8px;
  padding: 8px 10px;
  text-align: left;
  font-size: 0.83rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.5);
    color: #e0a15e;
  }
`;

const BoardScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 1140px;
`;

const Column = styled(motion.section)<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.5)' : 'rgba(255, 255, 255, 0.08)')};
  background: linear-gradient(180deg, rgba(24, 27, 34, 0.95), rgba(18, 20, 28, 0.95));
  border-radius: 14px;
  min-height: 62vh;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
`;

const ColumnHead = styled.div`
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const ColumnTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #e8eaed;
  font-weight: 800;
`;

const Count = styled.span`
  color: #9aa4b2;
  font-size: 0.9rem;
  font-weight: 800;
`;

const ColumnBody = styled.div`
  padding: 10px;
  overflow-y: auto;
  max-height: 72vh;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DropZoneHint = styled.div`
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px;
  color: #778399;
  font-size: 0.86rem;
  font-weight: 700;
  text-align: center;
`;

const Card = styled.article<{ $dragging?: boolean; $interactive?: boolean }>`
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 10px;
  display: grid;
  gap: 10px;
  opacity: ${({ $dragging }) => ($dragging ? 0.55 : 1)};
  cursor: ${({ $interactive = true }) => ($interactive ? 'grab' : 'default')};

  &:active {
    cursor: ${({ $interactive = true }) => ($interactive ? 'grabbing' : 'default')};
  }
`;

const CardTitle = styled.h4`
  margin: 0;
  color: #f4f6fa;
  font-size: 1rem;
  line-height: 1.35;
  font-weight: 800;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const CompleteToggle = styled.button<{ $done?: boolean; $disabled?: boolean }>`
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({ $done }) => ($done ? 'rgba(120, 192, 126, 0.95)' : 'rgba(255, 255, 255, 0.4)')};
  background: ${({ $done }) => ($done ? 'rgba(120, 192, 126, 0.9)' : 'transparent')};
  color: ${({ $done }) => ($done ? '#0f2c15' : '#dbe4f2')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  flex-shrink: 0;
  margin-top: 1px;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;

  &:hover {
    border-color: ${({ $done }) => ($done ? 'rgba(120, 192, 126, 0.95)' : 'rgba(224, 161, 94, 0.72)')};
  }
`;

const CardQuickRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-height: 24px;
`;

const QuickActionWrap = styled.div`
  position: relative;
`;

const QuickActionButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(255, 255, 255, 0.52);
  background: transparent;
  color: #d4ddec;
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.8);
    color: #e0a15e;
  }
`;

const QuickPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 50;
  min-width: 240px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(18, 22, 34, 0.98), rgba(10, 14, 25, 0.98));
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.45);
  padding: 8px;
  display: grid;
  gap: 6px;
`;

const QuickSearch = styled.input`
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  color: #e8edf6;
  padding: 8px 10px;
  font-size: 0.82rem;
  font-weight: 700;
  outline: none;
`;

const QuickList = styled.div`
  display: grid;
  gap: 4px;
  max-height: 220px;
  overflow-y: auto;
`;

const QuickListItem = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  background: transparent;
  color: #e7ecf6;
  padding: 8px 10px;
  text-align: left;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  display: grid;
  gap: 2px;

  span {
    color: #9dacbf;
    font-size: 0.72rem;
    font-weight: 700;
  }

  &:hover {
    border-color: rgba(224, 161, 94, 0.7);
    color: #e0a15e;
  }
`;

const QuickListIdentity = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const QuickListText = styled.span`
  display: grid;
  gap: 2px;
  min-width: 0;

  strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const QuickDateInput = styled.input`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  color: #e8edf6;
  padding: 8px 10px;
  font-size: 0.82rem;
  font-weight: 800;
  outline: none;
`;

const QuickLinkButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #e8eaed;
  border-radius: 9px;
  padding: 6px 10px;
  font-size: 0.8rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.34);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const QuickEmpty = styled.div`
  border: 1px dashed rgba(255, 255, 255, 0.24);
  border-radius: 10px;
  padding: 8px 10px;
  color: #9dacbf;
  font-size: 0.8rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 24px;
  color: #9aa4b2;
  font-weight: 700;
  text-align: center;
`;

const DraftTitleInput = styled.input`
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #f2f5f9;
  padding: 2px 0;
  font-size: 1rem;
  font-weight: 800;

  &::placeholder {
    color: rgba(188, 199, 214, 0.72);
  }

  &:focus {
    outline: none;
  }
`;

const DraftError = styled.div`
  border: 1px solid rgba(255, 107, 107, 0.4);
  background: rgba(255, 107, 107, 0.12);
  color: #ff9a9a;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.78rem;
  font-weight: 800;
`;

function tsToDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate() as Date;
  const seconds = value.seconds ?? value._seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDateInput(value: any): string {
  const date = tsToDate(value);
  if (!date) return '';
  try {
    return convertFromUTC(date, getUserTimezone() || 'UTC').localDate;
  } catch {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

function toTicketDueIso(dateInput: string): string | null {
  const trimmed = String(dateInput || '').trim();
  if (!trimmed) return null;
  const timezone = getUserTimezone() || 'UTC';
  const utcDate = convertToUTC(trimmed, '23:59', timezone);
  return Number.isNaN(utcDate.getTime()) ? null : utcDate.toISOString();
}

function ticketDueValue(ticket: any): any {
  return ticket?.plannedEndAt ?? ticket?.slaResolutionDueAt ?? null;
}

function priorityRank(priority?: string): number {
  if (priority === 'P0') return 4;
  if (priority === 'P1') return 3;
  if (priority === 'P2') return 2;
  return 1;
}

function dueStamp(value: any): number {
  return tsToDate(value)?.getTime() ?? Number.MAX_SAFE_INTEGER;
}

function compareTickets(a: any, b: any, sortMode: SortMode): number {
  if (sortMode === 'due_desc') return dueStamp(ticketDueValue(b)) - dueStamp(ticketDueValue(a));
  if (sortMode === 'priority_desc') return priorityRank(b.priority) - priorityRank(a.priority);
  if (sortMode === 'title_asc') return String(a.title || '').localeCompare(String(b.title || ''));
  return dueStamp(ticketDueValue(a)) - dueStamp(ticketDueValue(b));
}

function filterLabel(value: PriorityFilter): string {
  return PRIORITY_FILTER_OPTIONS.find((option) => option.value === value)?.label ?? 'All priorities';
}

function sortLabel(value: SortMode): string {
  return SORT_OPTIONS.find((option) => option.value === value)?.label ?? 'Due date (soonest)';
}

function newClientMutationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mut_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const BOARD_STATUS_SET = new Set<BoardStatus>([
  'open',
  'in_progress',
  'blocked',
  'resolved',
]);

function toMs(value: any): number {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    try {
      const date = value.toDate();
      return date instanceof Date ? date.getTime() : 0;
    } catch {
      return 0;
    }
  }
  const seconds = value?.seconds ?? value?._seconds;
  if (typeof seconds === 'number') return seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value > 2_000_000_000 ? value : value * 1000;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

export default function AgentTicketsBoardPage() {
  const { user, orgId, role } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const viewerTimezone = useMemo(() => getUserTimezone() || 'UTC', []);

  const tickets = useSelector(selectAllTickets);

  const [hydrating, setHydrating] = useState(true);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [activeDropStatus, setActiveDropStatus] = useState<BoardStatus | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('due_asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [assignees, setAssignees] = useState<TicketAssigneeOption[]>([]);
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [quickMenu, setQuickMenu] = useState<{
    ticketId: string;
    type: 'assignee' | 'due';
  } | null>(null);
  const [dueDateInput, setDueDateInput] = useState('');
  const filterRef = useRef<HTMLDivElement | null>(null);
  const sortRef = useRef<HTMLDivElement | null>(null);
  const draftInputRef = useRef<HTMLInputElement | null>(null);
  const cursorRef = useRef<string | null>(null);
  const syncInFlightRef = useRef(false);
  const flushInFlightRef = useRef(false);
  const ticketsRef = useRef<any[]>([]);
  const pendingStatusRef = useRef<Map<string, { status: BoardStatus; createdAt: number }>>(
    new Map(),
  );

  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);

  const fetchCurrentTickets = useCallback(async (options?: { silent?: boolean }) => {
    if (!user?.uid) return;
    const silent = Boolean(options?.silent);

    if (!silent) setHydrating(true);
    try {
      await dispatch(
        fetchTickets({
          assignedAgentId: user.uid,
          status: CURRENT_STATUSES,
          orderBy: 'plannedEndAt',
          direction: 'asc',
          limit: 200,
        } as any)
      ).unwrap();
    } finally {
      if (!silent) setHydrating(false);
    }
  }, [dispatch, user?.uid]);

  const fetchAssignees = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const response = await apiFetch<{ assignees?: TicketAssigneeOption[] }>(
        '/api/tickets/assignees',
      );
      const incoming = Array.isArray(response?.assignees)
        ? response.assignees
        : [];
      const next = incoming
        .filter((item) => item?.uid)
        .map((item) => ({
          uid: String(item.uid),
          name: String(item.name || item.email || item.uid),
          email: String(item.email || ''),
          photoURL: item.photoURL ?? null,
          title: item.title ?? null,
          role: item.role ?? null,
        }));
      if (user?.uid && !next.some((item) => item.uid === user.uid)) {
        next.unshift({
          uid: user.uid,
          name: user.displayName || user.email || 'You',
          email: user.email || '',
          photoURL: user.photoURL || null,
          title: null,
          role: role || null,
        });
      }
      setAssignees(next);
    } catch {
      setAssignees([]);
    }
  }, [role, user?.displayName, user?.email, user?.photoURL, user?.uid]);

  const applyIncomingTicket = useCallback(
    (incoming: any) => {
      const ticketId = String(incoming?.id || '').trim();
      if (!ticketId) return;

      const status = String(incoming?.status || '') as BoardStatus;
      if (!BOARD_STATUS_SET.has(status)) {
        dispatch(ticketRemoved(ticketId));
        return;
      }

      const assigned = incoming?.assignedAgentId
        ? String(incoming.assignedAgentId)
        : '';
      if (user?.uid) {
        const me = String(user.uid);
        const isPrimary = assigned === me;
        const secondaryAgentIds = Array.isArray(incoming?.secondaryAgentIds)
          ? incoming.secondaryAgentIds.map((value: unknown) => String(value))
          : [];
        const isSecondary = secondaryAgentIds.includes(me);
        if (!isPrimary && !isSecondary) {
          dispatch(ticketRemoved(ticketId));
          return;
        }
      }

      const pending = pendingStatusRef.current.get(ticketId);
      if (pending) {
        if (Date.now() - pending.createdAt > 30000) {
          pendingStatusRef.current.delete(ticketId);
        } else if (pending.status !== status) {
          return;
        } else {
          pendingStatusRef.current.delete(ticketId);
        }
      }

      const current = ticketsRef.current.find(
        (ticket) => String(ticket.id) === ticketId,
      );
      const incomingMs = toMs(incoming?.updatedAt ?? incoming?.createdAt);
      const currentMs = toMs(
        (current as any)?.updatedAt ?? (current as any)?.createdAt,
      );
      if (current && incomingMs && currentMs && incomingMs < currentMs) {
        return;
      }

      dispatch(ticketUpserted(incoming));
    },
    [dispatch, user?.uid],
  );

  const syncTicketChanges = useCallback(async () => {
    if (!user?.uid || syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    try {
      let hasMore = true;
      let loops = 0;
      while (hasMore && loops < 4) {
        loops += 1;
        const usp = new URLSearchParams({
          assignedAgentId: user.uid,
          limit: '120',
        });
        if (cursorRef.current) usp.set('since', cursorRef.current);
        const response = await apiFetch<{
          changes?: Array<{
            id: string;
            ticketId: string;
            deleted?: boolean;
            ticket?: any;
          }>;
          nextCursor?: string | null;
          hasMore?: boolean;
        }>(`/api/tickets/changes?${usp.toString()}`);

        const changes = Array.isArray(response?.changes) ? response.changes : [];
        for (const change of changes) {
          if (change?.deleted || !change?.ticket) {
            if (change?.ticketId) dispatch(ticketRemoved(change.ticketId));
            continue;
          }
          applyIncomingTicket(change.ticket);
        }

        if (response?.nextCursor) {
          cursorRef.current = String(response.nextCursor);
        }

        hasMore = Boolean(response?.hasMore) && changes.length > 0;
        if (!changes.length) break;
      }
      setSyncError(null);
    } catch (error: any) {
      setSyncError(error?.message || 'Realtime sync failed.');
    } finally {
      syncInFlightRef.current = false;
    }
  }, [applyIncomingTicket, dispatch, user?.uid]);

  const reconcileRealtimeChange = useCallback(
    async (payload: {
      ticketId?: string;
      deleted?: boolean;
    }) => {
      const ticketId = String(payload?.ticketId || '').trim();
      if (!ticketId) return;
      if (payload?.deleted) {
        dispatch(ticketRemoved(ticketId));
        return;
      }
      try {
        const ticket = await apiFetch<any>(`/api/tickets/${ticketId}`);
        if (!ticket?.id) {
          dispatch(ticketRemoved(ticketId));
          return;
        }
        applyIncomingTicket(ticket);
      } catch {
        dispatch(ticketRemoved(ticketId));
      }
    },
    [applyIncomingTicket, dispatch],
  );

  const processOutboxMutation = useCallback(async (mutation: TicketMutationRecord) => {
    if (mutation.kind === 'create') {
      const payload = mutation.payload || {};
      const created = await apiFetch<{ id: string }>('/api/tickets', {
        method: 'POST',
        body: JSON.stringify({
          title: String(payload.title || 'Untitled'),
          description: '',
          priority: payload.priority || 'P2',
          orgId: payload.orgId || undefined,
          assignedAgentId: payload.assignedAgentId || undefined,
          clientMutationId: mutation.id,
        }),
      });
      if (created?.id) {
        const ticket = await apiFetch<any>(`/api/tickets/${created.id}`);
        if (ticket?.id) {
          if (payload.tempId) {
            dispatch(ticketReplaced({ tempId: String(payload.tempId), ticket }));
          } else {
            dispatch(ticketUpserted(ticket));
          }
        }
      }
      if (payload.tempId && !created?.id) {
        dispatch(ticketRemoved(String(payload.tempId)));
      }
      return;
    }

    if (mutation.kind === 'status') {
      const payload = mutation.payload || {};
      if (!payload.ticketId || String(payload.ticketId).startsWith('temp:')) {
        return;
      }
      await apiFetch(`/api/tickets/${payload.ticketId}/transition`, {
        method: 'POST',
        body: JSON.stringify({
          status: payload.status,
          clientMutationId: mutation.id,
        }),
      });
      return;
    }

    if (mutation.kind === 'assign') {
      const payload = mutation.payload || {};
      if (!payload.ticketId || String(payload.ticketId).startsWith('temp:')) {
        return;
      }
      await apiFetch(`/api/tickets/${payload.ticketId}/assign`, {
        method: 'POST',
        body: JSON.stringify({
          agentId: payload.agentId ?? null,
          teamId: payload.teamId ?? null,
          clientMutationId: mutation.id,
        }),
      });
      return;
    }

    if (mutation.kind === 'patch') {
      const payload = mutation.payload || {};
      if (!payload.ticketId || String(payload.ticketId).startsWith('temp:')) {
        return;
      }
      const fields = payload.fields && typeof payload.fields === 'object'
        ? payload.fields
        : {};
      const patched = await apiFetch<any>(`/api/tickets/${payload.ticketId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...fields,
          clientMutationId: mutation.id,
        }),
      });
      if (patched?.id) {
        dispatch(ticketUpserted(patched));
      }
    }
  }, [dispatch]);

  const flushOutbox = useCallback(async () => {
    if (flushInFlightRef.current) return;
    flushInFlightRef.current = true;
    try {
      const ready = await listReadyTicketMutations(40);
      const latestTicketMutation = new Map<string, string>();
      for (const mutation of ready) {
        if (mutation.kind !== 'status' && mutation.kind !== 'assign') continue;
        const ticketId = String(mutation.payload?.ticketId || '').trim();
        if (!ticketId) continue;
        latestTicketMutation.set(`${mutation.kind}:${ticketId}`, mutation.id);
      }

      let touched = false;
      for (const mutation of ready) {
        if (mutation.kind === 'status' || mutation.kind === 'assign') {
          const ticketId = String(mutation.payload?.ticketId || '').trim();
          if (ticketId) {
            const latestId = latestTicketMutation.get(`${mutation.kind}:${ticketId}`);
            if (latestId && latestId !== mutation.id) {
              touched = true;
              await markTicketMutationDone(mutation.id);
              continue;
            }
          }
        }

        try {
          await processOutboxMutation(mutation);
          await markTicketMutationDone(mutation.id);
          touched = true;
          if (mutation.kind === 'status') {
            const ticketId = String(mutation.payload?.ticketId || '').trim();
            const expectedStatus = String(mutation.payload?.status || '').trim() as BoardStatus;
            const pending = pendingStatusRef.current.get(ticketId);
            if (pending && pending.status === expectedStatus) {
              pendingStatusRef.current.delete(ticketId);
            }
          }
        } catch (error: any) {
          await markTicketMutationRetry(
            mutation.id,
            error?.message || 'ticket_mutation_failed',
          );
        }
      }
      if (touched) {
        void syncTicketChanges();
      }
    } finally {
      flushInFlightRef.current = false;
    }
  }, [processOutboxMutation, syncTicketChanges]);

  useEffect(() => {
    void dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    cursorRef.current = `${new Date().toISOString()}|~`;
    void fetchCurrentTickets();
    void fetchAssignees();
    void flushOutbox();
  }, [fetchAssignees, fetchCurrentTickets, flushOutbox]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filterRef.current && !filterRef.current.contains(target)) setFilterOpen(false);
      if (sortRef.current && !sortRef.current.contains(target)) setSortOpen(false);
      if (quickMenu) {
        const rootId = `${quickMenu.ticketId}:${quickMenu.type}`;
        const inQuickMenu = (target as HTMLElement)?.closest?.(
          `[data-quick-root="${rootId}"]`,
        );
        if (!inQuickMenu) {
          setQuickMenu(null);
          setAssigneeQuery('');
        }
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [quickMenu]);

  useEffect(() => {
    if (!draftOpen) return;
    const timer = window.setTimeout(() => draftInputRef.current?.focus(), 0);
    const onEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || draftBusy) return;
      setDraftOpen(false);
      setDraftTitle('');
      setDraftError(null);
    };
    window.addEventListener('keydown', onEsc);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onEsc);
    };
  }, [draftBusy, draftOpen]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void syncTicketChanges();
      void flushOutbox();
    }, 8000);
    return () => window.clearInterval(interval);
  }, [flushOutbox, syncTicketChanges]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void fetchCurrentTickets({ silent: true });
    }, 60000);
    return () => window.clearInterval(interval);
  }, [fetchCurrentTickets]);

  useEffect(() => {
    const onOnline = () => {
      void flushOutbox();
      void syncTicketChanges();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flushOutbox, syncTicketChanges]);

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | null = null;
    void subscribeTicketsRealtime((payload) => {
      if (disposed) return;
      void reconcileRealtimeChange(payload);
    }).then((off) => {
      if (disposed) {
        off();
        return;
      }
      unsubscribe = off;
    });
    return () => {
      disposed = true;
      if (unsubscribe) unsubscribe();
    };
  }, [reconcileRealtimeChange]);

  useEffect(() => {
    if (hydrating) return;
    void syncTicketChanges();
  }, [hydrating, syncTicketChanges]);

  const visibleTickets = useMemo(() => {
    const filtered = tickets.filter((ticket) => {
      if (priorityFilter === 'all') return true;
      return ticket.priority === priorityFilter;
    });
    return [...filtered].sort((a, b) => compareTickets(a, b, sortMode));
  }, [priorityFilter, sortMode, tickets]);

  const filteredAssignees = useMemo(() => {
    const q = assigneeQuery.trim().toLowerCase();
    if (!q) return assignees;
    return assignees.filter((assignee) => {
      const haystack = `${assignee.name} ${assignee.email} ${assignee.title ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [assigneeQuery, assignees]);

  const assigneeById = useMemo(() => {
    const next = new Map<string, TicketAssigneeOption>();
    for (const assignee of assignees) {
      const uid = String(assignee.uid ?? '').trim();
      if (!uid) continue;
      next.set(uid, assignee);
    }
    return next;
  }, [assignees]);

  const resolveTicketAssignee = useCallback(
    (ticket: any) => {
      const uid = String(ticket?.assignedAgentId ?? '').trim();
      if (!uid) return null;

      const option = assigneeById.get(uid);
      const firstName = String(ticket?.assignedAgentFirstName ?? '').trim();
      const lastName = String(ticket?.assignedAgentLastName ?? '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const displayName =
        option?.name ||
        String(ticket?.assignedAgentDisplayName ?? '').trim() ||
        fullName ||
        String(ticket?.assignedAgentEmail ?? '').trim() ||
        uid;

      return {
        uid,
        name: displayName,
        email:
          option?.email ||
          String(ticket?.assignedAgentEmail ?? '').trim() ||
          '',
        photoURL:
          option?.photoURL ??
          ticket?.assignedAgentPhotoURL ??
          null,
      };
    },
    [assigneeById],
  );

  const boardData = useMemo(() => {
    const buckets: Record<BoardStatus, any[]> = {
      open: [],
      in_progress: [],
      blocked: [],
      resolved: [],
    };

    for (const ticket of visibleTickets) {
      const status = ticket.status as BoardStatus;
      if (!buckets[status]) continue;
      buckets[status].push(ticket);
    }

    for (const key of Object.keys(buckets) as BoardStatus[]) {
      buckets[key].sort((a, b) => compareTickets(a, b, sortMode));
    }

    return buckets;
  }, [sortMode, visibleTickets]);

  const openEditor = useCallback((id: string) => {
    dispatch(openTicketPortal({ ticketId: id }));
  }, [dispatch]);

  const moveTicket = useCallback(
    async (ticketId: string, toStatus: BoardStatus) => {
      const current = ticketsRef.current.find((ticket) => ticket.id === ticketId);
      if (!current) return;
      if ((current.status as BoardStatus) === toStatus) return;
      if (String(ticketId).startsWith('temp:')) return;

      dispatch(
        ticketUpserted({
          ...current,
          status: toStatus,
        } as any),
      );
      pendingStatusRef.current.set(ticketId, {
        status: toStatus,
        createdAt: Date.now(),
      });

      try {
        await enqueueTicketMutation({
          id: newClientMutationId(),
          kind: 'status',
          payload: {
            ticketId,
            status: toStatus,
          },
        });
        void flushOutbox();
      } catch (error) {
        pendingStatusRef.current.delete(ticketId);
        dispatch(ticketUpserted(current as any));
      }
    },
    [dispatch, flushOutbox]
  );

  const toggleCardCompleted = useCallback(
    async (event: React.MouseEvent, ticket: any) => {
      event.preventDefault();
      event.stopPropagation();
      const currentStatus = String(ticket?.status || '') as BoardStatus;
      if (!ticket?.id || String(ticket.id).startsWith('temp:')) return;
      const nextStatus: BoardStatus =
        currentStatus === 'resolved' ? 'open' : 'resolved';
      await moveTicket(ticket.id, nextStatus);
    },
    [moveTicket],
  );

  const quickAssignOwner = useCallback(
    async (ticketId: string, agentId: string | null) => {
      const current = ticketsRef.current.find((ticket) => String(ticket.id) === String(ticketId));
      if (!current || String(ticketId).startsWith('temp:')) return;
      const nextAssignee = agentId ? assigneeById.get(agentId) ?? null : null;
      dispatch(
        ticketUpserted({
          ...current,
          assignedAgentId: agentId,
          assignedAgentDisplayName: nextAssignee?.name ?? null,
          assignedAgentEmail: nextAssignee?.email ?? null,
          assignedAgentPhotoURL: nextAssignee?.photoURL ?? null,
        } as any),
      );
      try {
        await enqueueTicketMutation({
          id: newClientMutationId(),
          kind: 'assign',
          payload: {
            ticketId,
            agentId,
          },
        });
        void flushOutbox();
      } catch {
        dispatch(ticketUpserted(current as any));
      }
    },
    [assigneeById, dispatch, flushOutbox],
  );

  const quickSetDueDate = useCallback(
    async (ticketId: string, dateInput: string) => {
      const current = ticketsRef.current.find((ticket) => String(ticket.id) === String(ticketId));
      if (!current || String(ticketId).startsWith('temp:')) return;
      const dueIso = toTicketDueIso(dateInput);
      const fields = {
        dueDate: dateInput || null,
        timezone: viewerTimezone,
      };
      dispatch(
        ticketUpserted({
          ...current,
          plannedEndAt: dueIso,
          timezone: viewerTimezone,
        } as any),
      );
      try {
        await enqueueTicketMutation({
          id: newClientMutationId(),
          kind: 'patch',
          payload: {
            ticketId,
            fields,
          },
        });
        void flushOutbox();
      } catch {
        dispatch(ticketUpserted(current as any));
      }
    },
    [dispatch, flushOutbox, viewerTimezone],
  );

  const onDragStart = useCallback((event: React.DragEvent<HTMLElement>, ticketId: string) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ticketId);
    setDraggingId(ticketId);
  }, []);

  const onDragEnd = useCallback(() => {
    setDraggingId(null);
    setActiveDropStatus(null);
  }, []);

  const onDropToColumn = useCallback(
    async (event: React.DragEvent<HTMLElement>, columnStatus: BoardStatus) => {
      event.preventDefault();
      const ticketId = event.dataTransfer.getData('text/plain');
      setActiveDropStatus(null);
      setDraggingId(null);
      if (!ticketId) return;
      await moveTicket(ticketId, columnStatus);
    },
    [moveTicket]
  );

  const total = tickets.length;
  const visible = visibleTickets.length;

  const startDraftCard = () => {
    if (draftOpen) {
      draftInputRef.current?.focus();
      return;
    }
    setPriorityFilter('all');
    setSortMode('due_asc');
    setDraftError(null);
    setDraftTitle('');
    setDraftOpen(true);
  };

  const openAssigneeMenu = (event: React.MouseEvent, ticketId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setAssigneeQuery('');
    setQuickMenu((previous) => {
      if (previous?.ticketId === ticketId && previous.type === 'assignee') {
        return null;
      }
      return { ticketId, type: 'assignee' };
    });
  };

  const openDueMenu = (event: React.MouseEvent, ticket: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDueDateInput(formatDateInput(ticketDueValue(ticket)));
    setQuickMenu((previous) => {
      if (previous?.ticketId === ticket?.id && previous?.type === 'due') {
        return null;
      }
      return { ticketId: String(ticket?.id || ''), type: 'due' };
    });
  };

  const persistDraftTicket = useCallback(async () => {
    if (draftBusy || !draftOpen || !user?.uid) return;
    const title = draftTitle.trim();

    if (!title) {
      setDraftOpen(false);
      setDraftTitle('');
      setDraftError(null);
      return;
    }

    setDraftBusy(true);
    setDraftError(null);
    let tempId: string | null = null;
    try {
      const mutationId = newClientMutationId();
      tempId = `temp:${mutationId}`;
      dispatch(
        ticketUpserted({
          id: tempId,
          title,
          description: '',
          status: 'open',
          priority: 'P2',
          clientId: '' as any,
          assignedAgentId: user.uid,
          projectId: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          __optimistic: true,
        } as any),
      );

      await enqueueTicketMutation({
        id: mutationId,
        kind: 'create',
        payload: {
          title,
          priority: 'P2',
          orgId: orgId ?? null,
          assignedAgentId: user.uid,
          tempId,
        },
      });
      void flushOutbox();
      setSyncError(null);
      setDraftOpen(false);
      setDraftTitle('');
    } catch (error: any) {
      if (tempId) dispatch(ticketRemoved(tempId));
      setDraftError(error?.message || 'Failed to create ticket.');
    } finally {
      setDraftBusy(false);
    }
  }, [dispatch, draftBusy, draftOpen, draftTitle, flushOutbox, orgId, user?.uid]);

  return (
    <Page>
      <Header>
        <div>
          <Heading>Board</Heading>
          <Subtitle>Same tickets as List, visualized as a Kanban workflow.</Subtitle>
        </div>
        <Summary>{visible}/{total} current tickets</Summary>
      </Header>

      <Toolbar>
        <ToolbarLeft>
          <PrimaryAction type="button" onClick={startDraftCard}>
            <Plus size={15} />
            Create ticket
          </PrimaryAction>
        </ToolbarLeft>

        <ToolbarRight>
          <MenuWrap ref={filterRef}>
            <MenuButton
              type="button"
              onClick={() => {
                setFilterOpen((prev) => !prev);
                setSortOpen(false);
              }}
            >
              <Filter size={14} />
              <MenuLabel>Filter</MenuLabel>
              <span>{filterLabel(priorityFilter)}</span>
              <ChevronDown size={14} />
            </MenuButton>
            {filterOpen ? (
              <MenuPanel>
                {PRIORITY_FILTER_OPTIONS.map((option) => (
                  <MenuOption
                    key={option.value}
                    type="button"
                    $active={option.value === priorityFilter}
                    onClick={() => {
                      setPriorityFilter(option.value);
                      setFilterOpen(false);
                    }}
                  >
                    {option.label}
                  </MenuOption>
                ))}
              </MenuPanel>
            ) : null}
          </MenuWrap>

          <MenuWrap ref={sortRef}>
            <MenuButton
              type="button"
              onClick={() => {
                setSortOpen((prev) => !prev);
                setFilterOpen(false);
              }}
            >
              <ArrowUpDown size={14} />
              <MenuLabel>Sort</MenuLabel>
              <span>{sortLabel(sortMode)}</span>
              <ChevronDown size={14} />
            </MenuButton>
            {sortOpen ? (
              <MenuPanel>
                {SORT_OPTIONS.map((option) => (
                  <MenuOption
                    key={option.value}
                    type="button"
                    $active={option.value === sortMode}
                    onClick={() => {
                      setSortMode(option.value);
                      setSortOpen(false);
                    }}
                  >
                    {option.label}
                  </MenuOption>
                ))}
              </MenuPanel>
            ) : null}
          </MenuWrap>
        </ToolbarRight>
      </Toolbar>

      {syncError ? <DropZoneHint>{syncError}</DropZoneHint> : null}

      <BoardScroll>
        <BoardGrid>
          {BOARD_COLUMNS.map((column) => {
            const items = boardData[column.key] ?? [];
            return (
              <Column
                key={column.key}
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                $active={activeDropStatus === column.key}
                onDragOver={(event) => {
                  event.preventDefault();
                  setActiveDropStatus(column.key);
                }}
                onDragLeave={() => {
                  setActiveDropStatus((prev) => (prev === column.key ? null : prev));
                }}
                onDrop={(event) => void onDropToColumn(event, column.key)}
              >
                <ColumnHead>
                  <ColumnTitle>{column.label}</ColumnTitle>
                  <Count>{items.length}</Count>
                </ColumnHead>
                <ColumnBody>
                  {column.key === 'open' && draftOpen ? (
                    <Card $dragging={false} $interactive={false}>
                      <CardTop>
                        <CompleteToggle type="button" $disabled aria-hidden>
                          <Check size={12} />
                        </CompleteToggle>
                        <DraftTitleInput
                          ref={draftInputRef}
                          value={draftTitle}
                          onChange={(event) => setDraftTitle(event.target.value)}
                          onBlur={() => {
                            void persistDraftTicket();
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void persistDraftTicket();
                            }
                            if (event.key === 'Escape' && !draftBusy) {
                              event.preventDefault();
                              setDraftOpen(false);
                              setDraftTitle('');
                              setDraftError(null);
                            }
                          }}
                          placeholder="Provide a ticket title..."
                          disabled={draftBusy}
                        />
                      </CardTop>
                      <CardQuickRow>
                        <QuickActionButton as="span" aria-hidden>
                          <UserPlus size={12} />
                        </QuickActionButton>
                        <QuickActionButton as="span" aria-hidden>
                          <CalendarDays size={12} />
                        </QuickActionButton>
                      </CardQuickRow>
                      {draftError ? <DraftError>{draftError}</DraftError> : null}
                    </Card>
                  ) : null}

                  {hydrating ? (
                    <DropZoneHint>Loading...</DropZoneHint>
                  ) : items.length === 0 && !(column.key === 'open' && draftOpen) ? (
                    <DropZoneHint>{column.empty}</DropZoneHint>
                  ) : (
                    items.map((ticket) => (
                      (() => {
                        const isOptimistic =
                          String(ticket.id).startsWith('temp:') ||
                          Boolean((ticket as any).__optimistic);
                        const isResolved = String(ticket.status || '') === 'resolved';
                        const assignedOwner = resolveTicketAssignee(ticket);
                        const quickRootAssignee = `${ticket.id}:assignee`;
                        const quickRootDue = `${ticket.id}:due`;
                        return (
                          <Card
                            key={ticket.id}
                            draggable={!isOptimistic}
                            onClick={() => {
                              if (!isOptimistic) openEditor(ticket.id);
                            }}
                            onDragStart={(event) => {
                              if (!isOptimistic) onDragStart(event, ticket.id);
                            }}
                            onDragEnd={onDragEnd}
                            $dragging={draggingId === ticket.id}
                            $interactive={!isOptimistic}
                          >
                            <CardTop>
                              <CompleteToggle
                                type="button"
                                $done={isResolved}
                                $disabled={isOptimistic}
                                onClick={(event) => {
                                  if (isOptimistic) return;
                                  void toggleCardCompleted(event, ticket);
                                }}
                              >
                                <Check size={12} />
                              </CompleteToggle>
                              <CardTitle>
                                {ticket.ticketNumber ? (
                                  <span style={{ color: '#8DEBFF', marginRight: 8 }}>#{ticket.ticketNumber}</span>
                                ) : null}
                                {ticket.title || 'Untitled ticket'}
                              </CardTitle>
                            </CardTop>

                            <CardQuickRow>
                              <QuickActionWrap data-quick-root={quickRootAssignee}>
                                <QuickActionButton
                                  type="button"
                                  onClick={(event) => openAssigneeMenu(event, ticket.id)}
                                  disabled={isOptimistic}
                                  aria-label={assignedOwner ? `Assigned to ${assignedOwner.name}` : 'Assign owner'}
                                  title={assignedOwner ? assignedOwner.name : 'Assign owner'}
                                >
                                  {assignedOwner ? (
                                    <PortalAvatar
                                      uid={assignedOwner.uid}
                                      email={assignedOwner.email}
                                      name={assignedOwner.name}
                                      photoURL={assignedOwner.photoURL}
                                      size={18}
                                      disableNavigation
                                    />
                                  ) : (
                                    <UserPlus size={12} />
                                  )}
                                </QuickActionButton>
                                {quickMenu?.ticketId === ticket.id && quickMenu?.type === 'assignee' ? (
                                  <QuickPopover onClick={(event) => event.stopPropagation()}>
                                    <QuickSearch
                                      value={assigneeQuery}
                                      onChange={(event) => setAssigneeQuery(event.target.value)}
                                      placeholder="Search owners..."
                                    />
                                    <QuickList>
                                      <QuickListItem
                                        type="button"
                                        onClick={() => {
                                          void quickAssignOwner(ticket.id, null);
                                          setQuickMenu(null);
                                          setAssigneeQuery('');
                                        }}
                                      >
                                        Unassigned
                                        <span>Remove current owner</span>
                                      </QuickListItem>
                                      {filteredAssignees.length ? (
                                        filteredAssignees.map((assignee) => (
                                          <QuickListItem
                                            key={`${ticket.id}-${assignee.uid}`}
                                            type="button"
                                            onClick={() => {
                                              void quickAssignOwner(ticket.id, assignee.uid);
                                              setQuickMenu(null);
                                              setAssigneeQuery('');
                                            }}
                                          >
                                            <QuickListIdentity>
                                              <PortalAvatar
                                                uid={assignee.uid}
                                                email={assignee.email}
                                                name={assignee.name}
                                                photoURL={assignee.photoURL ?? null}
                                                size={28}
                                                disableNavigation
                                              />
                                              <QuickListText>
                                                <strong>{assignee.name}</strong>
                                                <span>{assignee.email}</span>
                                              </QuickListText>
                                            </QuickListIdentity>
                                          </QuickListItem>
                                        ))
                                      ) : (
                                        <QuickEmpty>No available owners.</QuickEmpty>
                                      )}
                                    </QuickList>
                                  </QuickPopover>
                                ) : null}
                              </QuickActionWrap>

                              <QuickActionWrap data-quick-root={quickRootDue}>
                                <QuickActionButton
                                  type="button"
                                  onClick={(event) => openDueMenu(event, ticket)}
                                  disabled={isOptimistic}
                                  aria-label="Set due date"
                                >
                                  <CalendarDays size={12} />
                                </QuickActionButton>
                                {quickMenu?.ticketId === ticket.id && quickMenu?.type === 'due' ? (
                                  <QuickPopover onClick={(event) => event.stopPropagation()}>
                                    <QuickDateInput
                                      type="date"
                                      value={dueDateInput}
                                      onChange={(event) => {
                                        const next = event.target.value;
                                        setDueDateInput(next);
                                        void quickSetDueDate(ticket.id, next);
                                        setQuickMenu(null);
                                      }}
                                    />
                                    <QuickLinkButton
                                      type="button"
                                      onClick={() => {
                                        setDueDateInput('');
                                        void quickSetDueDate(ticket.id, '');
                                        setQuickMenu(null);
                                      }}
                                    >
                                      Clear due date
                                    </QuickLinkButton>
                                  </QuickPopover>
                                ) : null}
                              </QuickActionWrap>
                            </CardQuickRow>
                          </Card>
                        );
                      })()
                    ))
                  )}
                </ColumnBody>
              </Column>
            );
          })}
        </BoardGrid>
      </BoardScroll>

      {!hydrating && total === 0 ? <EmptyState>No current assigned tickets found.</EmptyState> : null}
      {!hydrating && total > 0 && visible === 0 ? <EmptyState>No tickets match current filters.</EmptyState> : null}
    </Page>
  );
}
